<template>
  <figure ref="host" class="relative w-full">
    <div class="relative" :style="{ height: `${height}px` }">
      <svg
        v-if="geo"
        :viewBox="`0 0 ${geo.width} ${geo.height}`"
        :width="geo.width"
        :height="geo.height"
        class="block overflow-visible"
        role="img"
        :aria-label="ariaLabel"
      >
        <defs>
          <!-- The fill fades out downwards; the line itself keeps a steady colour. -->
          <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style="stop-color: rgb(var(--cr-beam)); stop-opacity: 0.32" />
            <stop offset="70%" style="stop-color: rgb(var(--cr-beam)); stop-opacity: 0.06" />
            <stop offset="100%" style="stop-color: rgb(var(--cr-beam)); stop-opacity: 0" />
          </linearGradient>
        </defs>

        <!-- Faint horizontal rules, no labels: the tooltip carries the numbers. -->
        <line
          v-for="(rule, index) in geo.rules"
          :key="`rule-${index}`"
          :x1="geo.pad.left"
          :x2="geo.width - geo.pad.right"
          :y1="rule"
          :y2="rule"
          class="telemetry-grid"
        />

        <!-- Amber spike stems are drawn under the line so the line stays readable. -->
        <g v-for="spike in geo.spikes" :key="`spike-${spike.index}`">
          <line
            :x1="spike.x"
            :x2="spike.x"
            :y1="spike.baseY"
            :y2="spike.y"
            class="telemetry-spike"
            stroke-width="1.5"
          />
          <circle :cx="spike.x" :cy="spike.y" r="2.6" class="telemetry-spike-cap" />
        </g>

        <path :d="geo.area" :fill="`url(#${gradientId})`" />
        <!-- A wide, low-opacity copy of the line stands in for a glow without a blur filter. -->
        <path :d="geo.line" fill="none" class="telemetry-glow" stroke-width="7" />
        <path :d="geo.line" fill="none" class="telemetry-line" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />

        <!-- Crosshair -->
        <g v-if="hovered">
          <line
            :x1="hovered.x"
            :x2="hovered.x"
            :y1="geo.pad.top"
            :y2="geo.baseY"
            class="telemetry-crosshair"
            stroke-width="1"
          />
          <circle :cx="hovered.x" :cy="hovered.y" r="3.5" class="telemetry-dot" />
        </g>

        <rect
          :x="geo.pad.left"
          :y="geo.pad.top"
          :width="Math.max(0, geo.width - geo.pad.left - geo.pad.right)"
          :height="Math.max(0, geo.baseY - geo.pad.top)"
          fill="transparent"
          @pointermove="onPointerMove"
          @pointerleave="hoverIndex = null"
        />
      </svg>

      <!-- The max value is the only y-axis label, sitting where the reference puts it. -->
      <p v-if="geo" class="cr-muted absolute left-1 top-0 text-[11px] nums" aria-hidden="true">
        {{ geo.max.toLocaleString() }}
      </p>

      <div
        v-if="hovered"
        class="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-surface px-3 py-2 shadow-pop"
        :style="{ left: `${hovered.tooltipX}px`, top: `${Math.max(hovered.y - 10, 44)}px` }"
      >
        <p class="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">
          {{ hovered.dateLabel }}
        </p>
        <p class="mt-1 text-sm font-semibold text-gray-900 nums">
          {{ hovered.messageLabel }} <span class="font-normal text-gray-500">messages</span>
        </p>
        <p class="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
          <span class="h-1.5 w-1.5 rounded-full" :class="hovered.isSpike ? 'bg-warning-500' : 'bg-gray-300'"></span>
          {{ hovered.failureLabel }} failure rate
        </p>
      </div>
    </div>

    <div class="mt-2 flex justify-between gap-2 text-[11px] nums text-gray-500" aria-hidden="true">
      <span v-for="label in xLabels" :key="label.key">{{ label.text }}</span>
    </div>
  </figure>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useId } from 'vue';
import type { TelemetryPoint } from '@/types/charts';

/**
 * Message volume over time, with failure days marked by a vertical spike.
 *
 * The curve is a monotone cubic (Fritsch–Carlson): smooth, and it never overshoots into a
 * value that was not measured. The two segments that touch a failure spike are drawn as
 * straight lines instead, so a peak stays pointy while the rest of the series stays soft.
 */
const props = withDefaults(
  defineProps<{ points: TelemetryPoint[]; height?: number }>(),
  { height: 280 },
);

/**
 * A day counts as a spike when at least 5% of its messages failed DMARC. This is a display
 * threshold for finding the shape of the month, not a security rating.
 */
const SPIKE_THRESHOLD = 5;

const host = ref<HTMLElement | null>(null);
const width = ref(880);
const hoverIndex = ref<number | null>(null);
const gradientId = useId();

let observer: ResizeObserver | undefined;

onMounted(() => {
  if (!host.value) return;
  observer = new ResizeObserver(([entry]) => {
    width.value = entry.contentRect.width;
  });
  observer.observe(host.value);
  width.value = host.value.clientWidth || width.value;
});

onUnmounted(() => observer?.disconnect());

const pad = { top: 22, right: 6, bottom: 6, left: 6 };

const parseIsoDate = (iso: string): Date => {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
};

const shortDate = (iso: string) =>
  parseIsoDate(iso).toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });

const longDate = (iso: string) =>
  parseIsoDate(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

/** Round the axis up to a value that leaves the peak clear of the top edge. */
const niceMax = (value: number): number => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10]) {
    if (step * magnitude >= value) return Math.round(step * magnitude * 1000) / 1000;
  }
  return magnitude * 10;
};

const geo = computed(() => {
  const points = props.points;
  if (points.length === 0) return null;

  const w = Math.max(width.value, 240);
  const h = props.height;
  const baseY = h - pad.bottom;
  const max = niceMax(Math.max(...points.map((point) => point.messageCount)));
  const span = w - pad.left - pad.right;
  const step = points.length > 1 ? span / (points.length - 1) : 0;

  const x = (index: number) => pad.left + index * step;
  const y = (value: number) => pad.top + (1 - value / max) * (baseY - pad.top);

  const px = points.map((point, index) => ({ x: x(index), y: y(point.messageCount) }));
  const isSpike = points.map(
    (point) => point.failureRate >= SPIKE_THRESHOLD && point.messageCount > 0,
  );

  // Fritsch–Carlson tangents, in pixel space.
  const slopes: number[] = [];
  for (let i = 0; i < px.length - 1; i++) {
    slopes.push((px[i + 1].y - px[i].y) / Math.max(px[i + 1].x - px[i].x, 0.0001));
  }

  const tangents: number[] = new Array(px.length).fill(0);
  if (px.length === 2) {
    tangents[0] = slopes[0] ?? 0;
    tangents[1] = slopes[0] ?? 0;
  } else if (px.length > 2) {
    tangents[0] = slopes[0] ?? 0;
    tangents[px.length - 1] = slopes[slopes.length - 1] ?? 0;
    for (let i = 1; i < px.length - 1; i++) {
      const previous = slopes[i - 1];
      const next = slopes[i];
      tangents[i] = previous * next <= 0 ? 0 : (previous + next) / 2;
    }
    for (let i = 0; i < slopes.length; i++) {
      if (slopes[i] === 0) {
        tangents[i] = 0;
        tangents[i + 1] = 0;
        continue;
      }
      const a = tangents[i] / slopes[i];
      const b = tangents[i + 1] / slopes[i];
      const magnitude = a * a + b * b;
      if (magnitude > 9) {
        const scale = 3 / Math.sqrt(magnitude);
        tangents[i] = scale * a * slopes[i];
        tangents[i + 1] = scale * b * slopes[i];
      }
    }
  }

  let line = `M ${px[0].x} ${px[0].y}`;
  for (let i = 1; i < px.length; i++) {
    const previous = px[i - 1];
    const current = px[i];
    // Straight segments around a spike are what keep the apex a corner.
    if (isSpike[i] || isSpike[i - 1]) {
      line += ` L ${current.x} ${current.y}`;
      continue;
    }
    const third = (current.x - previous.x) / 3;
    const c1x = previous.x + third;
    const c1y = previous.y + tangents[i - 1] * third;
    const c2x = current.x - third;
    const c2y = current.y - tangents[i] * third;
    line += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${current.x} ${current.y}`;
  }

  const area = `${line} L ${px[px.length - 1].x} ${baseY} L ${px[0].x} ${baseY} Z`;

  const spikes = points.flatMap((_, index) =>
    isSpike[index]
      ? [{ index, x: px[index].x, y: px[index].y, baseY }]
      : [],
  );

  const rules = [0.25, 0.5, 0.75, 1].map((fraction) => y(max * fraction));

  return { width: w, height: h, baseY, max, pad, area, line, spikes, rules, px, points, isSpike, step, x };
});

const hovered = computed(() => {
  if (!geo.value || hoverIndex.value === null) return null;
  const index = hoverIndex.value;
  const point = geo.value.points[index];
  const position = geo.value.px[index];
  if (!point) return null;

  const tooltipX = Math.min(Math.max(position.x, 88), geo.value.width - 88);

  return {
    x: position.x,
    y: position.y,
    tooltipX,
    dateLabel: longDate(point.date),
    messageLabel: point.messageCount.toLocaleString(),
    failureLabel: `${point.failureRate}%`,
    isSpike: geo.value.isSpike[index],
  };
});

const onPointerMove = (event: PointerEvent) => {
  if (!geo.value) return;
  const bounds = (event.currentTarget as SVGRectElement).getBoundingClientRect();
  // Map through the viewBox scale: the svg is laid out at the measured width, but a resize
  // between the observer firing and the pointer event would otherwise shift the index.
  const scale = bounds.width > 0 ? geo.value.width / bounds.width : 1;
  const x = (event.clientX - bounds.left) * scale;
  const index = geo.value.step > 0 ? Math.round(x / geo.value.step) : 0;
  hoverIndex.value = Math.min(Math.max(index, 0), props.points.length - 1);
};

const xLabels = computed(() => {
  const count = props.points.length;
  if (count === 0) return [];
  const dense = width.value >= 480 && count >= 12;
  const wanted = dense
    ? [0, Math.floor((count - 1) / 4), Math.floor((count - 1) / 2), Math.floor(((count - 1) * 3) / 4), count - 1]
    : [0, Math.floor((count - 1) / 2), count - 1];
  return [...new Set(wanted)].map((index) => ({
    key: props.points[index].date,
    text: shortDate(props.points[index].date),
  }));
});

const ariaLabel = computed(() => {
  const spikes = props.points.filter(
    (point) => point.failureRate >= SPIKE_THRESHOLD && point.messageCount > 0,
  ).length;
  return `Message volume for ${props.points.length} days with ${spikes} failure ${
    spikes === 1 ? 'spike' : 'spikes'
  }. Hover a day for the exact counts.`;
});
</script>

<style scoped>
@reference "../style.css";

.telemetry-grid {
  stroke: rgb(var(--cr-grid) / 0.12);
  stroke-width: 1;
}

.telemetry-line {
  stroke: rgb(var(--cr-beam));
}

.telemetry-glow {
  stroke: rgb(var(--cr-beam) / 0.16);
}

.telemetry-spike {
  stroke: rgb(var(--cr-flare) / 0.75);
}

.telemetry-spike-cap {
  fill: rgb(var(--cr-flare));
}

.telemetry-crosshair {
  stroke: rgb(var(--cr-beam) / 0.45);
  stroke-dasharray: 3 4;
}

.telemetry-dot {
  fill: rgb(var(--cr-beam));
  stroke: rgb(var(--surface));
  stroke-width: 2;
}
</style>
